/* eslint-disable react/prop-types */
import React, { useContext, useState, useEffect } from 'react';
import {
  Nav,
  OverlayTrigger,
  Tooltip,
  Spinner,
  Alert,
  Button
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FaLink } from 'react-icons/fa';
import { MdOutlineSignalWifiOff } from 'react-icons/md';
import ProfileDropdown from 'components/navbar/top/ProfileDropdown';
import AppContext from 'context/Context';
import { getTenantFromToken } from 'utils/routeUtils';
import { toast } from 'react-toastify';

// Import services using the new optimized structure
import { handleOfflineDownload } from 'utils/offlineUtils';
import offlineDBService from 'db/dexieDB';
import offlineDownloadService from 'db/offlineDownloadService';
import { globalEventBus, GLOBAL_EVENTS } from 'utils/eventEmitter';
import { isAuthError, SYNC_EVENTS, syncData, syncEvents } from 'db/syncService';
import MapboxDownloadModal from 'screens/common/dynamicForm/dynamicNormalForm/FormFields/OfflineMapModal/OfflineMap';
import UserInfo from './UserInfo';

const TopNavRightSideNavItem = () => {
  const {
    config: { isDark, isRTL },
    setConfig
  } = useContext(AppContext);

  const tenant = getTenantFromToken();

  // State for tracking action status
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionType, setActionType] = useState(null); // 'sync' or 'download'
  const [hasUnsyncedData, setHasUnsyncedData] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showMapAlert, setShowMapAlert] = useState(false);

  const domain = window?.location?.hostname || '';
  const clientName = domain.split('.')[0]?.toUpperCase();

  // Check for unsynced data on initial load
  useEffect(() => {
    checkUnsyncedData();
  }, []);

  // Function to refresh dropdown data
  const refreshDropdownData = async () => {
    try {
      console.log('Refreshing dropdown data after sync...');

      // Check if we're online
      if (!navigator.onLine) {
        console.log('Cannot refresh dropdown data while offline');
        return false;
      }

      // Download fresh dropdown data
      const dropdownResult =
        await offlineDownloadService.downloadDropdownOptions(tenant);

      if (dropdownResult) {
        console.log('Dropdown data refreshed successfully');
        toast.success('Updated dropdown options from server');

        // Emit an event to notify other components that dropdown data has been updated
        globalEventBus.emit(GLOBAL_EVENTS.DROPDOWN_DATA_UPDATED);

        return true;
      } else {
        console.error('Failed to refresh dropdown data');
        return false;
      }
    } catch (error) {
      console.error('Error refreshing dropdown data:', error);
      return false;
    }
  };

  // Set up event listeners for sync and download events
  useEffect(() => {
    // Sync events
    const syncStartedHandler = () => {
      setActionType('sync');
      setIsProcessing(true);
    };

    const syncCompletedHandler = async result => {
      console.log('Sync completed, refreshing dropdown data...');

      // Always refresh dropdown data after successful sync
      await refreshDropdownData();

      setIsProcessing(false);
      setActionType(null);

      // Update UI based on sync result
      setHasUnsyncedData(result.stillHasUnsyncedData);

      // Show toast notification
      if (result.notification) {
        toast[result.notification.type](result.notification.message);
      }
    };

    const syncFailedHandler = async error => {
      console.log('Sync failed, but still refreshing dropdown data...');

      // Even if sync fails, try to refresh dropdown data
      await refreshDropdownData();

      setIsProcessing(false);
      setActionType(null);

      // Check for auth error
      if (error.error && isAuthError(error.error)) {
        toast.error('Your session has expired. Please log in again.');
      } else {
        toast.error(`Sync error: ${error.error || 'Unknown error'}`);
      }

      // Refresh unsynced data state
      checkUnsyncedData();
    };

    const authErrorHandler = ({ error }) => {
      setIsProcessing(false);
      setActionType(null);
      toast.error('Your session has expired. Please log in again.');
    };

    // Global database changed event
    const dbChangedHandler = () => {
      checkUnsyncedData();
    };

    // Register event listeners
    syncEvents.on(SYNC_EVENTS.STARTED, syncStartedHandler);
    syncEvents.on(SYNC_EVENTS.COMPLETED, syncCompletedHandler);
    syncEvents.on(SYNC_EVENTS.FAILED, syncFailedHandler);
    syncEvents.on(SYNC_EVENTS.AUTH_ERROR, authErrorHandler);
    globalEventBus.on(GLOBAL_EVENTS.DB_CHANGED, dbChangedHandler);

    // Add event listeners for DexieDB changes (backward compatibility)
    window.addEventListener('dexieDbChanged', dbChangedHandler);

    // Cleanup function
    return () => {
      syncEvents.off(SYNC_EVENTS.STARTED, syncStartedHandler);
      syncEvents.off(SYNC_EVENTS.COMPLETED, syncCompletedHandler);
      syncEvents.off(SYNC_EVENTS.FAILED, syncFailedHandler);
      syncEvents.off(SYNC_EVENTS.AUTH_ERROR, authErrorHandler);
      globalEventBus.off(GLOBAL_EVENTS.DB_CHANGED, dbChangedHandler);
      window.removeEventListener('dexieDbChanged', dbChangedHandler);
    };
  }, [tenant]); // Added tenant as dependency

  // Function to check for unsynced data
  const checkUnsyncedData = async () => {
    try {
      const data = await offlineDBService.getUnsyncedData();
      const hasData =
        data.farmers?.length > 0 ||
        data.farms?.length > 0 ||
        data.cultivations?.length > 0 ||
        data.forms?.length > 0;

      console.log('checkUnsyncedData result:', {
        hasData,
        farmerCount: data.farmers?.length || 0,
        farmCount: data.farms?.length || 0,
        cultivationCount: data.cultivations?.length || 0,
        formCount: data.forms?.length || 0
      });

      setHasUnsyncedData(hasData);
      return hasData;
    } catch (error) {
      console.error('Error checking unsynced data:', error);
      return false;
    }
  };

  // Function to handle syncing data
  const handleSync = async () => {
    try {
      // Call the sync service
      const result = await syncData(tenant);

      // Note: The dropdown refresh will happen in the event handlers above
      // This ensures it happens whether sync succeeds or fails

      return result;
    } catch (error) {
      console.error('Error in syncData:', error);
      setIsProcessing(false); // Ensure processing stops on error
      return {
        success: false,
        error: error.message,
        stillHasUnsyncedData: true
      };
    }
  };

  // Function to download application data (non-map data)
  const downloadAppData = async (showMapPrompt = false) => {
    try {
      setActionType('download');
      setIsProcessing(true);

      // Use the original offline download utility with proper parameters
      const result = await handleOfflineDownload({
        tenant,
        setIsDownloading: val => {
          // Only allow setting to false, we already set it to true above
          if (!val) {
            setIsProcessing(false);
          }
        },
        setDownloadSuccess: () => {
          console.log('Download success callback triggered');
        }
      });

      console.log('Application data download result:', result);

      setActionType(null);

      if (result && result.success) {
        toast.success('Data downloaded successfully for offline use.');

        // Show map download prompt if requested and download was successful
        if (showMapPrompt) {
          setShowMapAlert(true);
        }
      } else {
        const errorMsg =
          result && result.error ? result.error : 'Unknown error';
        toast.error(`Download error: ${errorMsg}`);

        // Ensure processing state is reset on failure
        setIsProcessing(false);
      }

      return result;
    } catch (error) {
      console.error('Error downloading application data:', error);

      // Check if this is an authentication error
      if (isAuthError(error)) {
        toast.error('Your session has expired. Please log in again.');
      } else {
        toast.error(`Download error: ${error.message}`);
      }

      // Ensure processing state is reset on error
      setIsProcessing(false);
      setActionType(null);

      return { success: false, error: error.message };
    }
  };

  // Handle map alert response
  const handleMapAlertYes = () => {
    setShowMapAlert(false);
    setShowMapModal(true);
  };

  // Improved unified handler for the button click
  const handleButtonClick = async () => {
    if (isProcessing) return;

    try {
      // Refresh check for unsynced data
      const initialCheck = await checkUnsyncedData();

      if (initialCheck) {
        // If there's unsynced data, ONLY sync it (no download)
        await handleSync();
        // Dropdown refresh will happen automatically in the event handlers
      } else {
        // No unsynced data, download app data directly
        downloadAppData(true); // Download with showMapPrompt=true
      }
    } catch (error) {
      console.error('Error handling button click:', error);

      // Check if this is an authentication error
      if (isAuthError(error)) {
        toast.error('Your session has expired. Please log in again.');
      } else {
        toast.error(`Error: ${error.message}`);
      }

      // Ensure processing state is reset on error
      setIsProcessing(false);
      setActionType(null);
    }
  };

  // Get the tooltip text based on current state
  const getTooltipText = () => {
    if (isProcessing) {
      return actionType === 'sync' ? 'Syncing data...' : 'Downloading data...';
    }
    return hasUnsyncedData ? 'Sync data' : 'Download offline data';
  };

  // Function to handle modal close
  const handleMapModalClose = () => {
    setShowMapModal(false);
  };

  return (
    <>
      <Nav
        navbar
        className="navbar-nav-icons ms-auto flex-row align-items-center"
        as="ul"
      >
        {/* Visit Website link (for RPLC) */}
        {clientName === 'RPLC' && (
          <Nav.Item
            as="li"
            style={{
              display: 'flex',
              flexDirection: 'row',
              marginRight: '10px'
            }}
          >
            <a
              href="https://rplcollaborative.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none text-primary d-flex align-items-center"
            >
              <FaLink
                style={{
                  marginRight: '5px',
                  width: '20px',
                  height: '20px'
                }}
              />
              <p
                className="m-0 fw-bold d-none d-md-inline"
                style={{ fontSize: '20px' }}
              >
                Visit website
              </p>
            </a>
          </Nav.Item>
        )}

        {/* Unified Sync/Download Button with Notification Dot */}
        {['TNS', 'MRV', 'HCLF', 'LOCALHOST'].includes(clientName) && (
          <Nav.Item as="li" style={{ marginRight: '10px' }}>
            <Nav.Link className="px-2 theme-control-toggle">
              <OverlayTrigger
                placement={isRTL ? 'bottom' : 'left'}
                overlay={
                  <Tooltip id="syncDownloadTooltip">{getTooltipText()}</Tooltip>
                }
              >
                <div
                  className="theme-control-toggle-label position-relative"
                  onClick={handleButtonClick}
                  style={{
                    cursor: isProcessing ? 'default' : 'pointer',
                    width: '37px',
                    height: '37px'
                  }}
                >
                  {/* Always show the download icon */}
                  <MdOutlineSignalWifiOff size={18} />

                  {/* Show notification dot only when there's data to sync */}
                  {hasUnsyncedData && (
                    <span
                      className="position-absolute bg-danger rounded-circle"
                      style={{
                        width: '8px',
                        height: '8px',
                        top: '0',
                        right: '0',
                        transform: 'translate(25%, -25%)'
                      }}
                    ></span>
                  )}

                  {/* Show spinner when processing */}
                  {isProcessing && (
                    <span
                      className="position-absolute bg-white rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: '18px',
                        height: '18px',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)'
                      }}
                    >
                      <Spinner animation="border" size="sm" />
                    </span>
                  )}
                </div>
              </OverlayTrigger>
            </Nav.Link>
          </Nav.Item>
        )}

        {/* Profile */}
        <UserInfo />
        <ProfileDropdown />
      </Nav>

      {/* Map Download Alert */}
      {showMapAlert && (
        <div
          className="position-fixed w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            top: 0,
            left: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1050
          }}
        >
          <Alert
            variant="info"
            className="m-3"
            style={{
              maxWidth: '400px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
            }}
            show={showMapAlert}
          >
            <Alert.Heading>Download Map for Offline Use</Alert.Heading>
            <p>
              Your data has been downloaded successfully. For full offline
              functionality, we recommend downloading map tiles for the areas
              you plan to work in.
            </p>
            <p>Would you like to download map tiles now?</p>
            <div className="d-flex justify-content-end">
              <Button variant="primary" onClick={handleMapAlertYes}>
                Yes
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {/* MapboxDownloadModal */}
      <MapboxDownloadModal
        show={showMapModal}
        onHide={handleMapModalClose}
        tenant={tenant}
        onDownloadComplete={null} // No need to download app data again
      />
    </>
  );
};

export default TopNavRightSideNavItem;
